import { useState, useMemo, useEffect } from "react";
import Sidebar from "../layout/Sidebar";
import Topbar from "../layout/Topbar";
import API from "../services/api";
import "../styles/UserManagement.css";
import Swal from "sweetalert2";
/* ─────────────────────── Constants ─────────────────────── */
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

/* ─────────────────────── Helpers ─────────────────────── */
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// DB stores 1/0; display as Active/Inactive
const isActive = (status) => Number(status) === 1;

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

/* ─────────────────────── Inline Styles ─────────────────────── */
const STYLES = `
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeIn  { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideUp { from { opacity:0; transform:translateY(30px) scale(.97); } to { opacity:1; transform:translateY(0) scale(1); } }

  /* ── Overlay loader ── */
  .um-overlay {
    position: absolute; inset: 0; z-index: 20;
    background: rgba(255,255,255,.75);
    display: flex; align-items: center; justify-content: center;
    border-radius: 14px; backdrop-filter: blur(2px);
  }
  .um-spinner {
    width: 38px; height: 38px;
    border: 3px solid #e5e7eb; border-top-color: #2563eb;
    border-radius: 50%; animation: spin .7s linear infinite;
  }

  /* ── Error / Success banners ── */
  .um-banner {
    border-radius: 10px; padding: .7rem 1rem;
    font-size: .82rem; font-weight: 600;
    display: flex; align-items: center; gap: .5rem;
    margin-bottom: 1rem; animation: fadeIn .3s ease;
  }
  .um-banner.err  { background:#fff5f5; border:1.5px solid #fecaca; color:#dc2626; }
  .um-banner.ok   { background:#f0fdf4; border:1.5px solid #86efac; color:#15803d; }
  .um-banner button {
    margin-left: auto; font-weight:700; cursor:pointer;
    background:none; border:none; color:inherit;
  }

  /* ── Skeleton shimmer ── */
  .skel-bar {
    height:13px; border-radius:6px;
    background: linear-gradient(90deg,#f0f2f5 25%,#e5e7eb 50%,#f0f2f5 75%);
    background-size:200% 100%; animation:shimmer 1.2s infinite;
  }
  @keyframes shimmer { to { background-position:-200% 0; } }

  /* ── Modal backdrop ── */
  .modal-backdrop {
    position:fixed; inset:0; z-index:1000;
    background:rgba(0,0,0,.45); backdrop-filter:blur(3px);
    display:flex; align-items:center; justify-content:center; padding:1rem;
  }
  .modal-box {
    background:#fff; border-radius:18px;
    width:100%; max-width:520px;
    box-shadow:0 24px 80px rgba(0,0,0,.22);
    animation:slideUp .25s ease; overflow:hidden;
  }
  .modal-hd {
    padding:1.1rem 1.4rem; border-bottom:1px solid #f0f2f5;
    display:flex; justify-content:space-between; align-items:center;
  }
  .modal-hd h3 { font-size:1rem; font-weight:700; color:#111827; }
  .modal-hd p  { font-size:.75rem; color:#9ca3af; margin-top:2px; }
  .modal-close {
    width:32px; height:32px; border-radius:8px;
    border:1.5px solid #e5e7eb; background:#fff;
    display:flex; align-items:center; justify-content:center;
    cursor:pointer; font-size:1rem; color:#6b7280;
    transition:background .12s;
  }
  .modal-close:hover { background:#f3f4f6; }
  .modal-body { padding:1.3rem 1.4rem; display:flex; flex-direction:column; gap:.9rem; }
  .modal-row  { display:grid; grid-template-columns:1fr 1fr; gap:.75rem; }

  .mfg { display:flex; flex-direction:column; gap:.3rem; }
  .mfl {
    font-size:.68rem; font-weight:700; letter-spacing:.07em;
    text-transform:uppercase; color:#374151;
  }
  .mfl.req::after { content:" *"; color:#dc2626; }
  .mfi, .mfs {
    width:100%; padding:.58rem .8rem;
    border:1.5px solid #e5e7eb; border-radius:9px;
    font-size:.875rem; font-family:inherit; color:#111827;
    background:#fff; outline:none;
    transition:border-color .15s, box-shadow .15s;
    -webkit-appearance:none; appearance:none;
  }
  .mfi::placeholder { color:#c0c4cc; }
  .mfi:focus, .mfs:focus {
    border-color:#6366f1; box-shadow:0 0 0 3px rgba(99,102,241,.1);
  }
  .mfi.merr, .mfs.merr {
    border-color:#f87171; box-shadow:0 0 0 3px rgba(239,68,68,.08);
  }
  .merr-msg { font-size:.7rem; color:#dc2626; margin-top:2px; font-weight:500; }

  .modal-err {
    background:#fff5f5; border:1.5px solid #fecaca;
    border-radius:9px; padding:.65rem .9rem;
    font-size:.8rem; color:#dc2626; font-weight:600;
  }

  .modal-ft {
    padding:1rem 1.4rem; border-top:1px solid #f0f2f5;
    display:flex; justify-content:flex-end; gap:.55rem;
  }
  .modal-cancel {
    padding:.52rem 1.15rem; border:1.5px solid #d1d5db; border-radius:9px;
    background:#fff; font-size:.875rem; font-weight:600;
    font-family:inherit; color:#374151; cursor:pointer;
    transition:background .12s;
  }
  .modal-cancel:hover { background:#f9fafb; }
  .modal-save {
    display:inline-flex; align-items:center; gap:.4rem;
    padding:.52rem 1.25rem; border:none; border-radius:9px;
    background:#2563eb; font-size:.875rem; font-weight:700;
    font-family:inherit; color:#fff; cursor:pointer;
    transition:background .12s;
  }
  .modal-save:hover:not(:disabled) { background:#1d4ed8; }
  .modal-save:disabled { background:#93c5fd; cursor:not-allowed; }

  /* password toggle button inside input */
  .pw-wrap { position:relative; }
  .pw-wrap .mfi { padding-right:2.4rem; }
  .pw-eye {
    position:absolute; right:.7rem; top:50%; transform:translateY(-50%);
    background:none; border:none; cursor:pointer; color:#9ca3af; padding:0;
    display:flex; align-items:center;
  }

  /* status toggle button in table */
  .btn-activate   { padding:.35rem .85rem; border:1.5px solid #86efac; border-radius:7px; background:#f0fdf4; color:#15803d; font-size:.75rem; font-weight:700; font-family:inherit; cursor:pointer; transition:background .12s; white-space:nowrap; }
  .btn-activate:hover   { background:#dcfce7; }
  .btn-deactivate { padding:.35rem .85rem; border:1.5px solid #fecaca; border-radius:7px; background:#fff5f5; color:#dc2626; font-size:.75rem; font-weight:700; font-family:inherit; cursor:pointer; transition:background .12s; white-space:nowrap; }
  .btn-deactivate:hover { background:#fee2e2; }
  .btn-deactivate:disabled, .btn-activate:disabled { opacity:.5; cursor:not-allowed; }

  /* filters loading */
  .filters-loading { opacity:.6; pointer-events:none; }

  /* empty state */
  .um-empty {
    display:flex; flex-direction:column; align-items:center;
    padding:3rem 1rem; color:#9ca3af; font-size:.9rem; gap:.5rem;
  }

  /* ── Create User Button ── */
  .btn-create-user {
    display:inline-flex; align-items:center; gap:.45rem;
    padding:.52rem 1.15rem; border:none; border-radius:10px;
    background:#2563eb; font-size:.875rem; font-weight:700;
    font-family:inherit; color:#fff; cursor:pointer;
    transition:background .15s, box-shadow .15s;
    white-space:nowrap;
    box-shadow: 0 1px 4px rgba(37,99,235,.25);
  }
  .btn-create-user:hover { background:#1d4ed8; box-shadow:0 3px 10px rgba(37,99,235,.3); }

  /* ── Mobile / Desktop visibility ── */
  @media (min-width: 769px) {
    .mobile-list { display: none !important; }
  }
  @media (max-width: 768px) {
    .desktop-only { display: none !important; }
  }

  /* ── Mobile card list container ── */
  .mobile-list {
    display: flex;
    flex-direction: column;
    gap: .75rem;
  }

  /* ── Individual mobile card ── */
  .mc {
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,.05);
  }

  /* Card top: avatar + name/email + status badge */
  .mc-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .85rem 1rem;
    border-bottom: 1px solid #f3f4f6;
    gap: .5rem;
  }
  .mc-head .mc-name-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .mc-head .mc-name {
    font-size: .875rem;
    font-weight: 700;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .mc-head .mc-email {
    font-size: .72rem;
    color: #6b7280;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Card info rows */
  .mc-info {
    padding: .65rem 1rem;
    display: flex;
    flex-direction: column;
    gap: .45rem;
    border-bottom: 1px solid #f3f4f6;
  }
  .mc-info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .5rem;
  }
  .mc-info-label {
    font-size: .72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: #9ca3af;
    flex-shrink: 0;
  }
  .mc-info-val {
    font-size: .82rem;
    color: #374151;
    font-weight: 500;
    text-align: right;
  }

  /* Card actions footer */
  .mc-actions {
    display: flex;
    gap: .5rem;
    padding: .75rem 1rem;
  }
  .mc-btn-edit {
    flex: 0 0 auto;
    padding: .48rem 1.1rem;
    border: 1.5px solid #d1d5db;
    border-radius: 8px;
    background: #fff;
    font-size: .8rem;
    font-weight: 700;
    font-family: inherit;
    color: #374151;
    cursor: pointer;
    transition: background .12s;
    white-space: nowrap;
  }
  .mc-btn-edit:hover:not(:disabled) { background: #f3f4f6; }
  .mc-btn-edit:disabled { opacity: .5; cursor: not-allowed; }
  .mc-btn-toggle {
    flex: 1;
    padding: .48rem .75rem;
    border-radius: 8px;
    font-size: .8rem;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    transition: background .12s;
    white-space: nowrap;
    text-align: center;
  }

  /* Mobile pagination */
  .mob-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: .75rem 1rem;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 12px;
    margin-top: .25rem;
  }
  .mob-pagination .page-info {
    font-size: .8rem;
    color: #6b7280;
    font-weight: 600;
  }
  .mob-pagination .page-nav {
    display: flex;
    align-items: center;
    gap: .25rem;
  }
  .mob-pagination .page-btn {
    width: 32px; height: 32px;
    border: 1.5px solid #e5e7eb;
    border-radius: 7px;
    background: #fff;
    font-size: .85rem;
    color: #374151;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background .12s;
  }
  .mob-pagination .page-btn:hover:not(:disabled) { background: #f3f4f6; }
  .mob-pagination .page-btn:disabled { opacity: .4; cursor: not-allowed; }
  .mob-pagination .page-counter {
    font-size: .8rem;
    font-weight: 700;
    color: #374151;
    padding: 0 .35rem;
    min-width: 40px;
    text-align: center;
  }
  .mob-rpp {
    display: flex;
    align-items: center;
    gap: .4rem;
    font-size: .75rem;
    color: #6b7280;
    font-weight: 600;
  }
  .mob-rpp select {
    padding: .25rem .4rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 6px;
    font-size: .75rem;
    font-family: inherit;
    background: #fff;
    color: #374151;
    cursor: pointer;
  }

  .um-table{
      width:100%;
      table-layout:auto;
  }
`;

/* ─────────────────────── Eye Icons ─────────────────────── */
const EyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const EyeOn = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

/* ─────────────────────── Create User Modal ─────────────────────── */
function CreateModal({ onClose, onCreated }) {
  const EMPTY = { name: "", email: "", phone: "", role: "staff", password: "" };
  const [form, setForm]             = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving]           = useState(false);
  const [showPw, setShowPw]           = useState(false);

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setFieldErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())     errs.name     = "Name is required.";
    if (!form.email.trim())    errs.email    = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email address.";
    if (!form.phone.trim())    errs.phone    = "Phone is required.";
    if (!form.role)            errs.role     = "Role is required.";
    if (!form.password.trim()) errs.password = "Password is required.";
    else if (form.password.length < 6) errs.password = "Password must be at least 6 characters.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };



  const handleCreate = async () => {
    setServerError("");
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await API.post("/user/create", {
        name:     form.name.trim(),
        email:    form.email.trim(),
        phone:    form.phone.trim(),
        role:     form.role,
        password: form.password,
      });
      if (res.data.status) {
        onCreated(res.data.data);
        onClose();
      } else {
        setServerError(res.data.message || "Creation failed.");
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.data && typeof data.data === "object") {
        // Laravel validation errors: { field: ["msg"] }
        const fe = {};
        Object.entries(data.data).forEach(([k, v]) => {
          fe[k] = Array.isArray(v) ? v[0] : v;
        });
        setFieldErrors(fe);
        setServerError("Please fix the errors below.");
      } else {
        setServerError(data?.message || "Something went wrong.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="modal-backdrop" onClick={onBackdrop}>
      <div className="modal-box" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="modal-hd">
          <div>
            <h3>Create User</h3>
            <p>Add a new Supervisor or Field Staff account</p>
          </div>
          <button className="modal-close" onClick={onClose} title="Close">✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {serverError && <div className="modal-err">{serverError}</div>}

          <div className="modal-row">
            <div className="mfg">
              <label className="mfl req">Full Name</label>
              <input
                className={`mfi${fieldErrors.name ? " merr" : ""}`}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Full name"
              />
              {fieldErrors.name && <span className="merr-msg">{fieldErrors.name}</span>}
            </div>
            <div className="mfg">
              <label className="mfl req">Email</label>
              <input
                className={`mfi${fieldErrors.email ? " merr" : ""}`}
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="email@example.com"
              />
              {fieldErrors.email && <span className="merr-msg">{fieldErrors.email}</span>}
            </div>
          </div>

          <div className="modal-row">
            <div className="mfg">
              <label className="mfl req">Phone</label>
              <input
                className={`mfi${fieldErrors.phone ? " merr" : ""}`}
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+973 3300 0000"
              />
              {fieldErrors.phone && <span className="merr-msg">{fieldErrors.phone}</span>}
            </div>
            <div className="mfg">
              <label className="mfl req">Role</label>
              <select
                className={`mfs${fieldErrors.role ? " merr" : ""}`}
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
              >
                <option value="admin">Admin</option>
                <option value="supervisor">Supervisor</option>
                <option value="team_leader">Team Leader</option>
                <option value="staff">Field Staff</option>
                <option value="manager">Manager</option>
              </select>
              {fieldErrors.role && <span className="merr-msg">{fieldErrors.role}</span>}
            </div>
          </div>

          <div className="mfg">
            <label className="mfl req">Password</label>
            <div className="pw-wrap">
              <input
                className={`mfi${fieldErrors.password ? " merr" : ""}`}
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder="Min. 6 characters"
              />
              <button type="button" className="pw-eye" onClick={() => setShowPw((p) => !p)}>
                {showPw ? <EyeOff /> : <EyeOn />}
              </button>
            </div>
            {fieldErrors.password && <span className="merr-msg">{fieldErrors.password}</span>}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-ft">
          <button className="modal-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="modal-save" onClick={handleCreate} disabled={saving}>
            {saving ? (
              <>
                <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.4)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin .7s linear infinite" }} />
                Creating…
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Create User
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Edit Modal ─────────────────────── */
function EditModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:     user.name     ?? "",
    email:    user.email    ?? "",
    phone:    user.phone    ?? "",
    role:     user.role     ?? "staff",
    status:   String(user.status ?? "1"),
    password: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving]           = useState(false);
  const [showPw, setShowPw]           = useState(false);

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setFieldErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())  errs.name  = "Name is required.";
    if (!form.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email address.";
    if (!form.phone.trim()) errs.phone = "Phone is required.";
    if (!form.role)         errs.role  = "Role is required.";
    if (form.password && form.password.length < 6) errs.password = "Password must be at least 6 characters.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    setServerError("");
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await API.post("/user/update", {
        user_id:  user.id,
        name:     form.name.trim(),
        email:    form.email.trim(),
        phone:    form.phone.trim(),
        role:     form.role,
        status:   form.status,
        password: form.password || undefined,
      });
      if (res.data.success) {
        onSaved(res.data.data);
        onClose();
      } else {
        setServerError(res.data.message || "Update failed.");
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) {
        const fe = {};
        Object.entries(data.errors).forEach(([k, v]) => { fe[k] = Array.isArray(v) ? v[0] : v; });
        setFieldErrors(fe);
        setServerError("Please fix the errors below.");
      } else {
        setServerError(data?.message || "Something went wrong.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="modal-backdrop" onClick={onBackdrop}>
      <div className="modal-box" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="modal-hd">
          <div>
            <h3>Edit User</h3>
            <p>Update details for <strong>{user.name}</strong></p>
          </div>
          <button className="modal-close" onClick={onClose} title="Close">✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {serverError && <div className="modal-err">{serverError}</div>}

          <div className="modal-row">
            <div className="mfg">
              <label className="mfl req">Full Name</label>
              <input className={`mfi${fieldErrors.name ? " merr" : ""}`}
                value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="Full name" />
              {fieldErrors.name && <span className="merr-msg">{fieldErrors.name}</span>}
            </div>
            <div className="mfg">
              <label className="mfl req">Email</label>
              <input className={`mfi${fieldErrors.email ? " merr" : ""}`}
                type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                placeholder="email@example.com" />
              {fieldErrors.email && <span className="merr-msg">{fieldErrors.email}</span>}
            </div>
          </div>

          <div className="modal-row">
            <div className="mfg">
              <label className="mfl req">Phone</label>
              <input className={`mfi${fieldErrors.phone ? " merr" : ""}`}
                value={form.phone} onChange={(e) => set("phone", e.target.value)}
                placeholder="+973 3300 0000" />
              {fieldErrors.phone && <span className="merr-msg">{fieldErrors.phone}</span>}
            </div>
            <div className="mfg">
              <label className="mfl req">Role</label>
              <select className={`mfs${fieldErrors.role ? " merr" : ""}`}
                value={form.role} onChange={(e) => set("role", e.target.value)}>
                <option value="admin">Admin</option>
                <option value="supervisor">Supervisor</option>
                <option value="team_leader">Team Leader</option>
                <option value="staff">Field Staff</option>
                <option value="manager">Manager</option>
              </select>
              {fieldErrors.role && <span className="merr-msg">{fieldErrors.role}</span>}
            </div>
          </div>

          <div className="modal-row">
            <div className="mfg">
              <label className="mfl req">Status</label>
              <select className="mfs" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div className="mfg">
              <label className="mfl">New Password <span style={{ color:"#9ca3af", fontWeight:400, textTransform:"none" }}>(optional)</span></label>
              <div className="pw-wrap">
                <input
                  className={`mfi${fieldErrors.password ? " merr" : ""}`}
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder="Leave blank to keep current"
                />
                <button type="button" className="pw-eye" onClick={() => setShowPw((p) => !p)}>
                  {showPw ? <EyeOff /> : <EyeOn />}
                </button>
              </div>
              {fieldErrors.password && <span className="merr-msg">{fieldErrors.password}</span>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-ft">
          <button className="modal-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="modal-save" onClick={handleSave} disabled={saving}>
            {saving
              ? <><span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.4)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin .7s linear infinite" }} /> Saving…</>
              : <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save Changes
                </>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Main Component ─────────────────────── */
function UserManagement() {
  const [showSidebar, setShowSidebar] = useState(false);

  /* ── Filter inputs ── */
  const [search,       setSearch]       = useState("");
  const [roleFilter,   setRoleFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  /* ── Pagination ── */
  const [page,        setPage]        = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* ── Sort (client-side on current page) ── */
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  /* ── Data ── */
  const [users,   setUsers]   = useState([]);
  const [counts,  setCounts]  = useState({ total_users:0, active_users:0, inactive_users:0, supervisors:0 });
  const [meta,    setMeta]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  /* ── Toast ── */
  const [toast, setToast] = useState(null);
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  /* ── Status toggle loading per user ── */
  const [togglingId, setTogglingId] = useState(null);

  /* ── Modals ── */
  const [editUser,      setEditUser]      = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  /* ─────────── Fetch ─────────── */
  const fetchUsers = async (overrides = {}) => {
    setLoading(true);
    setError("");

    const params = {
      page,
      per_page: rowsPerPage,
      search: search || undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      role: roleFilter !== "all" ? roleFilter : undefined,
      ...overrides,
    };

    Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);

    try {
      const res = await API.get("/users/list", { params });
      const payload = res.data;

      if (payload.success) {
        setUsers(payload.data.data ?? []);
        setMeta(payload.data);
        if (payload.counts) setCounts(payload.counts);
      } else {
        setError(payload.message || "Failed to load users.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ ADD HERE (INSIDE UserManagement)
const handleDeleteUser = async (userId) => {
  const result = await Swal.fire({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Yes, delete it!",
  });

  if (result.isConfirmed) {
    try {
      const res = await API.get(`/delete-user/${userId}`);

      if (res.data.success) {
        // ✅ update UI
        setUsers((prev) => prev.filter((u) => u.id !== userId));

        setCounts((c) => ({
          ...c,
          total_users: c.total_users - 1,
        }));

        Swal.fire("Deleted!", "User has been deleted.", "success");
      } else {
        Swal.fire("Error!", res.data.message || "Delete failed", "error");
      }
    } catch (err) {
      Swal.fire(
        "Error!",
        err?.response?.data?.message || "Something went wrong",
        "error"
      );
    }
  }
};

 useEffect(() => {fetchUsers();}, [page, rowsPerPage]); // eslint-disable-line

  const applyFilters = () => {
    setPage(1);
    fetchUsers({ page: 1 });
  };

  /* ─────────── Status Toggle ─────────── */
  const handleToggleStatus = async (user) => {
    const newStatus = isActive(user.status) ? 0 : 1;
    setTogglingId(user.id);
    try {
      const res = await API.post("/user/status", { user_id: user.id, status: newStatus });
      if (res.data.success) {
        setUsers((prev) =>
          prev.map((u) => u.id === user.id ? { ...u, status: newStatus } : u)
        );
        setCounts((c) => ({
          ...c,
          active_users:   newStatus === 1 ? c.active_users + 1 : c.active_users - 1,
          inactive_users: newStatus === 0 ? c.inactive_users + 1 : c.inactive_users - 1,
        }));
        showToast("ok", res.data.message || "Status updated.");
      } else {
        showToast("err", res.data.message || "Status update failed.");
      }
    } catch (err) {
      showToast("err", err?.response?.data?.message || "Something went wrong.");
    } finally {
      setTogglingId(null);
    }
  };

  /* ─────────── After Edit Save ─────────── */
  const handleUserSaved = (updatedUser) => {
    setUsers((prev) =>
      prev.map((u) => u.id === updatedUser.id ? { ...u, ...updatedUser } : u)
    );
    showToast("ok", "User updated successfully.");
  };

  /* ─────────── After Create ─────────── */
  const handleUserCreated = (newUser) => {
    // Prepend new user and update counts
    setUsers((prev) => [newUser, ...prev]);
    setCounts((c) => ({
      ...c,
      total_users:  c.total_users + 1,
      active_users: c.active_users + 1,
      ...(newUser.role === "supervisor" ? { supervisors: c.supervisors + 1 } : {}),
    }));
    showToast("ok", "User created successfully.");
  };

  /* ─────────── Sort ─────────── */
  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const displayed = useMemo(() => {
    if (!sortKey) return users;
    return [...users].sort((a, b) => {
      const av = String(a[sortKey] ?? "").toLowerCase();
      const bv = String(b[sortKey] ?? "").toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [users, sortKey, sortDir]);

  /* ─────────── Pagination meta from Laravel ─────────── */
  const totalPages   = meta?.last_page ?? 1;
  const totalRecords = meta?.total     ?? 0;
  const from         = meta?.from      ?? 0;
  const to           = meta?.to        ?? 0;

  /* ─────────── SortIcon ─────────── */
  const SortIcon = ({ col }) => (
    <span className={`sort-icon ${sortKey === col ? "sort-active" : "sort-idle"}`}>
      {sortKey === col ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
    </span>
  );

  const cols = [
    { key: "name",   label: "NAME"   },
    { key: "email",  label: "EMAIL"  },
    { key: "phone",  label: "PHONE"  },
    // { key: "created_at", label: "CREATED AT" },
    { key: "role",   label: "ROLE"   },
    { key: "status", label: "STATUS" },
  ];
  /* ─────────── Skeleton rows ─────────── */
  const SkeletonRows = () =>
    Array.from({ length: rowsPerPage }).map((_, i) => (
      <tr key={i}>
        <td><div style={{ display:"flex", alignItems:"center", gap:".6rem" }}>
          <div style={{ width:34, height:34, borderRadius:"50%", background:"#f0f2f5" }} />
          <div className="skel-bar" style={{ width:100 }} />
        </div></td>
        {[130, 110, 70, 60].map((w, j) => (
          <td key={j}><div className="skel-bar" style={{ width:w }} /></td>
        ))}
        <td><div className="skel-bar" style={{ width:130 }} /></td>
      </tr>
    ));

  /* ─────────── Render ─────────── */
  return (
    <>
      <style>{STYLES}</style>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleUserCreated}
        />
      )}

      {/* Edit Modal */}
      {editUser && (
        <EditModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={handleUserSaved}
        />
      )}

      <div className="app">
        <Sidebar show={showSidebar} closeSidebar={() => setShowSidebar(false)} />
        <div className="main">
          <Topbar toggleSidebar={() => setShowSidebar(true)} />

          <div className="page">

            {/* ── Header ── */}
            <div className="um-header" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:".75rem" }}>
              <div className="um-header-text">
                <h2 className="um-title">User Management</h2>
                <p className="um-subtitle">Manage Supervisors &amp; Field Staff accounts</p>
              </div>
              <button
                className="btn-create-user"
                onClick={() => setShowCreateModal(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Create User
              </button>
            </div>

            {/* ── Toast banner ── */}
            {toast && (
              <div className={`um-banner ${toast.type}`}>
                {toast.type === "ok"
                  ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                }
                {toast.msg}
                <button onClick={() => setToast(null)}>✕</button>
              </div>
            )}

            {/* ── Error banner ── */}
            {error && (
              <div className="um-banner err">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
                <button onClick={() => fetchUsers()}>Retry</button>
              </div>
            )}

            {/* ── Stats ── */}
            <div className="stats-row">
              {[
                { label: "Total Users",  value: counts.total_users,    cls: ""          },
                { label: "Active",       value: counts.active_users,   cls: "stat-green"},
                { label: "Inactive",     value: counts.inactive_users, cls: "stat-red"  },
                { label: "Supervisors",  value: counts.supervisors,    cls: "stat-blue" },
              ].map(({ label, value, cls }) => (
                <div key={label} className={`stat-card ${cls}`}>
                  <div className="stat-content">
                    <span className="stat-val">{value}</span>
                    <span className="stat-lbl">{label}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Filters ── */}
            <div className={`filters-bar${loading ? " filters-loading" : ""}`}>
              <div className="search-wrap">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" className="search-svg">
                  <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name, email, or phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                />
              </div>
              <div className="filter-selects">
                <select className="filter-select" value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}>
                  <option value="all">All Roles</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="staff">Field Staff</option>
                  <option value="manager">Manager</option>
                </select>
                <select className="filter-select" value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
                <button className="apply-btn" onClick={applyFilters} disabled={loading}
                  style={{ padding:".46rem 1rem", borderRadius:9, border:"1.5px solid #2563eb", background:"#2563eb", color:"#fff", fontWeight:700, fontSize:".82rem", fontFamily:"inherit", cursor:"pointer" }}>
                  {loading ? "Loading…" : "Apply"}
                </button>
              </div>
            </div>

            {/* ── Desktop Table ── */}
            <div className="table-card desktop-only" style={{ position:"relative" }}>

              {/* Loader overlay */}
              {loading && <div className="um-overlay"><div className="um-spinner" /></div>}

              <div className="table-scroll">
                <table className="um-table">
                  <thead>
                    <tr>
                      {cols.map(({ key, label }) => (
                        <th key={key} onClick={() => handleSort(key)} style={{ cursor:"pointer" }}>
                          {label} <SortIcon col={key} />
                        </th>
                      ))}
                      <th className="th-right">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SkeletonRows />
                    ) : displayed.length === 0 ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="um-empty">
                            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                            <span>No users match your filters.</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      displayed.map((user) => {
                        const active = isActive(user.status);
                        const toggling = togglingId === user.id;
                        return (
                          <tr key={user.id}>
                            {/* Name */}
                            <td>
                              <div className="name-cell">
                                <span className="td-name">{user.name}</span>
                              </div>
                            </td>

                            {/* Email */}
                            <td>
                              <a className="td-email" href={`mailto:${user.email}`}>{user.email}</a>
                            </td>

                            {/* Phone */}
                            <td className="td-muted">{user.phone || "—"}</td>

                            {/* Created At */}
                            {/* <td className="td-muted">
                              {formatDate(user.created_at)}
                            </td> */}

                            {/* Role */}
                            <td>
                              <span className={`badge ${user.role === "supervisor" || user.role === "Supervisor" ? "badge-blue" : "badge-gray"}`}>
                                {capitalize(user.role?.replace("_", " "))}
                              </span>
                            </td>

                            {/* Status */}
                            <td>
                              <span className={`badge ${active ? "badge-green" : "badge-red"}`}>
                                <span className="badge-dot" />
                                {active ? "Active" : "Inactive"}
                              </span>
                            </td>

                            {/* Actions */}
                            <td>
                            <div className="action-btns">
                              <button
                                className="btn-edit"
                                onClick={() => setEditUser(user)}
                                disabled={toggling}
                              >
                                Edit
                              </button>

                              <button
                                className={active ? "btn-deactivate" : "btn-activate"}
                                onClick={() => handleToggleStatus(user)}
                                disabled={toggling}
                              >
                                {toggling ? "…" : active ? "Deactivate" : "Activate"}
                              </button>

                              <button
                                style={{
                                  padding: ".35rem .85rem",
                                  border: "1.5px solid #fecaca",
                                  borderRadius: "7px",
                                  background: "#fff5f5",
                                  color: "#dc2626",
                                  fontSize: ".75rem",
                                  fontWeight: "700",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                Delete
                              </button>
                            </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination">
                <div className="rpp">
                  <span className="rpp-label">Rows per page:</span>
                  <select className="rpp-select" value={rowsPerPage}
                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
                    {ROWS_PER_PAGE_OPTIONS.map((n) => <option key={n}>{n}</option>)}
                  </select>
                </div>

                <span className="page-info">
                  {totalRecords === 0 ? "0 results" : `${from}–${to} of ${totalRecords}`}
                </span>

                <div className="page-nav">
                  <button className="page-btn" onClick={() => setPage(1)}           disabled={page===1||loading}>«</button>
                  <button className="page-btn" onClick={() => setPage((p)=>p-1)}   disabled={page===1||loading}>‹</button>
                  <span style={{ padding:"0 .4rem", fontSize:".8rem", color:"#374151", fontWeight:600 }}>{page}/{totalPages}</span>
                  <button className="page-btn" onClick={() => setPage((p)=>p+1)}   disabled={page===totalPages||loading}>›</button>
                  <button className="page-btn" onClick={() => setPage(totalPages)} disabled={page===totalPages||loading}>»</button>
                </div>
              </div>
            </div>

            {/* ── Mobile cards ── */}
            <div className="mobile-list">
              {loading ? (
                <div style={{ textAlign:"center", padding:"2.5rem" }}>
                  <div className="um-spinner" style={{ margin:"0 auto" }} />
                </div>
              ) : displayed.length === 0 ? (
                <div className="um-empty">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                  <span>No users match your filters.</span>
                </div>
              ) : (
                displayed.map((user) => {
                  const active = isActive(user.status);
                  const toggling = togglingId === user.id;
                  return (
                    <div key={user.id} className="mc">
                      {/* ── Card Header: avatar + name/email + status ── */}
                      <div className="mc-head">
                        <div style={{ display:"flex", alignItems:"center", gap:".65rem", minWidth:0, flex:1 }}>
                          <div style={{
                            width:38, height:38, borderRadius:"50%", flexShrink:0,
                            background: active ? "#dbeafe" : "#f3f4f6",
                            color: active ? "#1d4ed8" : "#6b7280",
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:".72rem", fontWeight:800,
                          }}>
                            {getInitials(user.name)}
                          </div>
                          <div className="mc-name-col" style={{ minWidth:0 }}>
                            <span className="mc-name">{user.name}</span>
                            <span className="mc-email">{user.email}</span>
                          </div>
                        </div>
                        <span className={`badge ${active ? "badge-green" : "badge-red"}`} style={{ flexShrink:0 }}>
                          <span className="badge-dot" />{active ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {/* ── Card Info Rows ── */}
                      <div className="mc-info">
                        <div className="mc-info-row">
                          <span className="mc-info-label">Phone</span>
                          <span className="mc-info-val">{user.phone || "—"}</span>
                        </div>
                        {/* <div className="mc-info-row">
                          <span className="mc-info-label">Created</span>
                          <span className="mc-info-val">
                            {formatDate(user.created_at)}
                          </span>
                        </div> */}
                        <div className="mc-info-row">
                          <span className="mc-info-label">Role</span>
                          <span className={`badge ${user.role === "supervisor" || user.role === "Supervisor" ? "badge-blue" : "badge-gray"}`}>
                            {capitalize(user.role?.replace("_"," "))}
                          </span>
                        </div>
                      </div>

                      {/* ── Card Actions ── */}
                      <div className="mc-actions">
                        <button
                          className="mc-btn-edit"
                          onClick={() => setEditUser(user)}
                          disabled={toggling}
                        >
                          Edit
                        </button>
                        <button
                          className={`mc-btn-toggle ${active ? "btn-deactivate" : "btn-activate"}`}
                          onClick={() => handleToggleStatus(user)}
                          disabled={toggling}
                        >
                          {toggling ? "…" : active ? "Deactivate" : "Activate"}
                        </button>
                        <button
                            style={{
                              padding: ".48rem .75rem",
                              border: "1.5px solid #fecaca",
                              borderRadius: "8px",
                              background: "#fff5f5",
                              color: "#dc2626",
                              fontSize: ".8rem",
                              fontWeight: "700",
                              cursor: "pointer",
                            }}
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            Delete
                          </button>
                      </div>
                    </div>
                  );
                })
              )}

              {/* ── Mobile Pagination (always shown when there's data) ── */}
              {totalRecords > 0 && (
                <div className="mob-pagination">
                  <div className="mob-rpp">
                    <span>Rows:</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                    >
                      {ROWS_PER_PAGE_OPTIONS.map((n) => <option key={n}>{n}</option>)}
                    </select>
                  </div>
                  <span className="page-info">
                    {totalRecords === 0 ? "0 results" : `${from}–${to} of ${totalRecords}`}
                  </span>
                  <div className="page-nav">
                    <button className="page-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 1 || loading}>‹</button>
                    <span className="page-counter">{page}/{totalPages}</span>
                    <button className="page-btn" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages || loading}>›</button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default UserManagement;